/**
 * Firebase Configuration
 * 
 * Industry-standard Firebase setup with proper environment variable handling
 * and error checking for production readiness.
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { getFunctions, Functions } from 'firebase/functions';

// Firebase configuration interface
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

// Environment validation
const validateEnvironment = (): FirebaseConfig => {
  const requiredVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID'
  ];

  const missing = requiredVars.filter(varName => !import.meta.env[varName]);

  if (missing.length > 0) {
    console.error('🚫 Missing Firebase environment variables:', missing);
    throw new Error(`Missing Firebase configuration: ${missing.join(', ')}`);
  }

  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  };
};

// Firebase configuration
const firebaseConfig = validateEnvironment();

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let analytics: Analytics | null = null;
let functions: Functions;

try {
  console.log('🔥 Initializing Firebase...');

  // Initialize Firebase App
  app = initializeApp(firebaseConfig);
  console.log('✅ Firebase App initialized');

  // Initialize Firebase Authentication
  auth = getAuth(app);
  console.log('✅ Firebase Auth initialized');

  // Initialize Cloud Firestore
  db = getFirestore(app);
  console.log('✅ Firestore initialized');

  // Initialize Analytics (only in production)
  if (firebaseConfig.measurementId && import.meta.env.PROD) {
    analytics = getAnalytics(app);
    console.log('✅ Firebase Analytics initialized');
  }

  // Initialize Firebase Functions
  functions = getFunctions(app);
  console.log('✅ Firebase Functions initialized');

  console.log('🚀 Firebase initialization complete');
} catch (error) {
  console.error('🚫 Firebase initialization failed:', error);
  throw error;
}

// Export Firebase services
export { app, auth, db, analytics, functions };

// Export configuration for debugging
export const getFirebaseConfig = () => ({
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  isProduction: import.meta.env.PROD,
  hasAnalytics: !!analytics
});

// Connection status checker
export const checkFirebaseConnection = async (): Promise<boolean> => {
  try {
    console.log('🔍 Testing Firebase connection...');

    // Enhanced connection test with detailed logging
    const testResult = await fetch(`https://firestore.googleapis.com/v1/projects/${firebaseConfig.projectId}/databases/(default)/documents`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    console.log('🔍 Connection test result:', {
      status: testResult.status,
      statusText: testResult.statusText,
      url: testResult.url
    });

    return testResult.ok;
  } catch (error) {
    console.error('🚫 Firebase connection check failed:', error);
    console.error('🚫 Detailed error:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      cause: error instanceof Error && 'cause' in error ? error.cause : undefined
    });
    return false;
  }
};

// Development helpers
if (import.meta.env.DEV) {
  // Enable Firestore offline persistence in development
  console.log('🛠️ Development mode: Firebase debugging enabled');

  // Log configuration (without sensitive data)
  console.log('🔧 Firebase Config:', {
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain,
    hasAnalytics: !!analytics
  });
}

// Add global debugging function for console testing
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).__debugFirebase = async () => {
    console.log('🚫=== FIREBASE DEBUG START ===');

    try {
      // Test 1: Basic config
      console.log('🔧 Config check:', getFirebaseConfig());

      // Test 2: Auth state
      console.log('🔐 Current auth user:', auth.currentUser);

      // Test 3: Connection test
      const connected = await checkFirebaseConnection();
      console.log('🌐 Connection status:', connected);

      // Test 4: Simple Firestore operation
      const { doc, getDoc } = await import('firebase/firestore');
      const testDoc = doc(db, 'test', 'connection');
      const docSnap = await getDoc(testDoc);
      console.log('📄 Firestore test read:', docSnap.exists());

    } catch (error) {
      console.error('🚫 Debug test failed:', error);
    }

    console.log('🚫=== FIREBASE DEBUG END ===');
  };

  console.log('📝 Run __debugFirebase() in console to test Firebase');
}
